import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import AnimatedAssetSelector from "../../common/components/AnimatedAssetSelector";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import { arrayOfStringsToArrayOfIdKeyObjects } from "../../common/utils/generalUtils";
import {
  createAnimatedIcon,
  getAnimatedIconNameComponentCategories,
  getCombinedName,
} from "../api/animatedIconsApi";
import {
  getAnimatedIconNameError,
  validAnimatedIconName,
  validAnimationData,
} from "../validations/animatedIconValidations";
import styles from "./CreateAnimatedIconPage.module.css";

function CreateAnimatedIconPage() {
  const content =
    "1. All lowercase characters\n2. No special characters except '_'\n3. No numbers";

  let history = useHistory();
  const location = useLocation();

  // Context state
  const { user } = useContext(UserContext);

  // page loading state
  const [isLoading, setIsLoading] = useState(false);

  // Input value states
  const [animatedIconCategories, setAnimatedIconCategories] = useState([]);
  const [animatedIconSizes, setAnimatedIconSizes] = useState([]);
  const [selectedAnimatedIconCategory, setSelectedAnimatedIconCategory] =
    useState("");
  const [selectedAnimatedIconSize, setSelectedAnimatedIconSize] = useState("");
  const [animatedIconName, setAnimatedIconName] = useState(null);
  const [animatedIconFile, setAnimatedIconFile] = useState(null);

  // Error states
  const [
    animatedIconCategoryDropdownError,
    setAnimatedIconCategoryDropdownError,
  ] = useState(null);
  const [animatedIconSizeDropdownError, setAnimatedIconSizeDropdownError] =
    useState(null);
  const [animatedIconNameError, setAnimatedIconNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  function buildNotification(animatedAssetId, name) {
    return {
      title: "Animated Icon added",
      content: `${getCombinedName(name)} has been added successfully`,
      ctaText: "View",
      animatedIconId: animatedAssetId,
    };
  }

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    // Check if AnimatedIcon Category is selected
    if (selectedAnimatedIconCategory) {
      setAnimatedIconCategoryDropdownError(null);
    } else {
      setAnimatedIconCategoryDropdownError(
        "AnimatedIcon category cannot be empty"
      );
    }

    // Check if AnimatedIcon Size is selected
    if (selectedAnimatedIconSize) {
      setAnimatedIconSizeDropdownError(null);
    } else {
      setAnimatedIconSizeDropdownError("AnimatedIcon size cannot be empty");
    }

    // Check if animatedIcon name passes validations
    if (validAnimatedIconName(animatedIconName)) {
      setAnimatedIconNameError(null);
    } else {
      setAnimatedIconNameError(getAnimatedIconNameError(animatedIconName));
    }

    //Check if animatedIcon file is selected
    if (animatedIconFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("AnimatedIcon file cannot be empty");
    }

    if (
      selectedAnimatedIconCategory &&
      selectedAnimatedIconSize &&
      validAnimatedIconName(animatedIconName) &&
      animatedIconFile
    ) {
      setIsLoading(true);

      var name = {
        animated_icon_category: selectedAnimatedIconCategory.key,
        animated_icon_size: selectedAnimatedIconSize.key,
        animated_icon_name: animatedIconName,
      };

      try {
        const animatedIconId = await createAnimatedIcon(
          name,
          animatedIconFile,
          user.uid
        );
        setIsLoading(false);
        history.push(`/${routes.ANIMATED_ASSETS}/${routes.ICONS}`, {
          notification: buildNotification(animatedIconId, name),
        });
      } catch (error) {
        setIsLoading(false);
        if (error.name === "FirebaseError") {
          console.log(error.message);
        } else if (
          error.message === "AnimatedIcon with the same name already exists"
        ) {
          setAnimatedIconNameError(error.message);
        } else {
          console.log(error);
        }
      }
    }
  };

  useEffect(() => {
    getAnimatedIconNameComponentCategories()
      .then((querySnapshot) => {
        querySnapshot.forEach(function (doc) {
          if (doc.id === "animated_icon_category") {
            setAnimatedIconCategories(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
          if (doc.id === "animated_icon_size") {
            setAnimatedIconSizes(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
        });
      })
      .catch((error) => {
        console.error(error);
      });
  }, [setAnimatedIconCategories, setAnimatedIconSizes, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <LoadingPage />
        </div>
      ) : null}
      <div className={styles.createPageContainer}>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumb>
            <Crumb
              text="animated icons"
              tagProps={{
                to: `/${routes.ANIMATED_ASSETS}/${routes.ICONS}`,
              }}
              as={Link}
            />
            <Separator />
            <Crumb
              text="add animated icon"
              tagProps={{
                to: `/${routes.ANIMATED_ASSETS}/${routes.ICONS}/${routes.CREATE}`,
              }}
              as={Link}
            />
          </Breadcrumb>
        </div>
        <div>
          <h3 className={styles.heading}>Add Animated Icon</h3>
          <div className={styles.flagContainer}>
            <Flag
              page
              info
              title="How to name your Animated Icon"
              content={content}
            />
          </div>
          <div className={styles.formRow}>
            <div className={styles.productCategoryContainer}>
              <p className={styles.inputLabel}>Animated Icon Category</p>
              <div className={styles.inputContainer}>
                <Dropdown
                  typeahead
                  items={animatedIconCategories}
                  onChange={(selectedAnimatedIcon) => {
                    setSelectedAnimatedIconCategory(selectedAnimatedIcon);
                    setAnimatedIconCategoryDropdownError(null);
                  }}
                  stretch
                  invalid={!!animatedIconCategoryDropdownError}
                />
              </div>
              {animatedIconCategoryDropdownError ? (
                <Flag error title={animatedIconCategoryDropdownError} />
              ) : null}
            </div>
            <div className={styles.animatedIconCategoryContainer}>
              <p className={styles.inputLabel}>Animated Icon Size</p>
              <div className={styles.inputContainer}>
                <Dropdown
                  items={animatedIconSizes}
                  onChange={(selectedAnimatedIconSize) => {
                    setSelectedAnimatedIconSize(selectedAnimatedIconSize);
                    setAnimatedIconSizeDropdownError(null);
                  }}
                  stretch
                  invalid={!!animatedIconSizeDropdownError}
                />
              </div>
              {animatedIconSizeDropdownError ? (
                <Flag error title={animatedIconSizeDropdownError} />
              ) : null}
            </div>
            <div className={styles.animatedIconNameContainer}>
              <p className={styles.inputLabel}>Animated Icon Name</p>
              <div className={styles.inputContainer}>
                <TextField
                  required={!!animatedIconNameError}
                  size="m"
                  stretch
                  placeholder="Animated Icon Name"
                  type="text"
                  invalid={!!animatedIconNameError}
                  onChange={(e) => {
                    setAnimatedIconName(e.target.value);
                    setAnimatedIconNameError(null);
                  }}
                />
              </div>
              {animatedIconNameError ? (
                <Flag error title={animatedIconNameError} />
              ) : null}
            </div>
          </div>

          <div className={styles.assetSelectorContainer}>
            <div className={styles.inputContainer}>
              <AnimatedAssetSelector
                heading="Animated Icon File"
                onAssetSelected={(file) => {
                  if (file) {
                    setAnimatedIconFile(file);
                    setAssetSelectorError(null);
                  }
                }}
                lottieRef={(instance) => {
                  if (instance.animationData) {
                    if (!validAnimationData(instance.animationData)) {
                      setAssetSelectorError(
                        "Invalid color names for Animated Icon"
                      );
                    }
                  }
                }}
              />
            </div>
            {assetSelectorError ? (
              <Flag error title={assetSelectorError} />
            ) : null}
          </div>
          <Stack>
            <Button primary onClick={handleSaveClick}>
              Save
            </Button>
            <Button secondary onClick={handeCancelClick}>
              Cancel
            </Button>
          </Stack>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CreateAnimatedIconPage;
